import { getFarm, deleteFarm, updateFarm, addFarm } from '@/adapters/master';
import { IDataModal } from '@/interfaces/data-modal';
import { IFarm, IFarmRes } from '@/interfaces/master';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Badge,
  Flex,
  Form,
  FormProps,
  Modal,
  notification,
  Radio,
  Select,
  Table,
  TableColumnsType,
  Button,
} from 'antd';
import { useEffect, useMemo, useState, useCallback, Suspense } from 'react';
import { uniqueId } from 'lodash';

const _modal: IDataModal<IFarm> = {
  open: false,
};

const FarmPage: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<IFarm[]>([]);
  const [action, setAction] = useState<'create' | 'update' | 'match'>('create');
  const [selected, setSelected] = useState<IFarm[]>([]);
  const [modal, setModal] = useState<IDataModal<IFarm>>({ ..._modal });
  const [form] = Form.useForm();
  const [tab, setTab] = useState<'create' | 'update'>('create');

  // const { data, refetch } = useQuery<IBrandRes, Error>({
  //   queryKey: ['search-brand-list'],
  //   queryFn: searchBrands,
  //   retry: 0,
  // });
  const { data, refetch } = useQuery<IFarmRes, Error>({
    queryKey: ['get-farm'],
    queryFn: getFarm,
    retry: 0,
  });

  const actionFarmMutation = useMutation({
    mutationFn: async (data: Partial<IFarm>) =>
      tab === 'create' ? await addFarm(data) : await updateFarm(data),
    mutationKey: tab === 'create' ? ['add-farm'] : ['update-farm'],
    onSuccess: data => {
      if (data.status === 'OK' && tab === 'create') {
        notification.success({ message: 'Sync Farm สำเร็จ' });
        refetch();
      } else if (data.status === 'OK' && tab === 'update') {
        notification.success({ message: 'Update Farm สำเร็จ' });
        refetch();
      } else {
        notification.error({ message: 'Sync Farm ไม่สำเร็จ' });
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const handleSyncSelectedFarms = async (selectedRows: any) => {
    await actionFarmMutation.mutateAsync(selectedRows);
  };

  const actionDeleteFarm = useMutation({
    mutationFn: async (data: Partial<IFarm>) => await deleteFarm(data),
    mutationKey: ['delete-farm'],
    onSuccess: data => {
      if (data.status === 'OK' && tab === 'update') {
        notification.success({ message: 'Delete Farm สำเร็จ' });
        refetch();
      } else {
        notification.error({ message: 'Delete Farm ไม่สำเร็จ' });
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  // const handleSyncTOERP = async () => {
  //   await actionTOERP.mutateAsync();
  // };

  const handleDeleteFarms = async (selectedRows: any) => {
    await actionDeleteFarm.mutateAsync(selectedRows);
  };

  const handleUpdateFarmCode = useCallback((item: IFarm) => {
    setModal({
      open: true,
      item: item,
    });
  }, []);

  const columns = useMemo(
    () =>
      [
        {
          title: 'ลำดับ',
          dataIndex: '',
          width: 40,
          align: 'center' as const,
          className: 'text-bold',
          render: (_: any, __: IFarm, i: number) => (
            <div className="font-weight-bold">{i + 1}</div>
          ),
        },
        {
          title: 'รหัสเกษตรกร',
          dataIndex: 'FARM_USER1',
          defaultSortOrder: 'ascend',
          sorter: (a, b) =>
            Number(a.FARM_USER1?.replace(/\D/g, '').replace('-', '')) -
            Number(b.FARM_USER1?.replace(/\D/g, '').replace('-', '')),
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.FARM_USER1)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.FARM_USER1?.startsWith(value as string);
          },
        },
        {
          title: 'ชื่อ-นามสกุล',
          dataIndex: 'FRM_NAME',
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.FRM_NAME)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.FRM_NAME?.startsWith(value as string);
          },
        },
        {
          title: 'ชื่อสวน',
          dataIndex: 'FARM_NAME',
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.FARM_NAME)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.FARM_NAME?.startsWith(value as string);
          },
        },
        {
          title: 'ตำบล',
          dataIndex: 'FARM_DISTRICT',
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.FARM_DISTRICT)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.FARM_DISTRICT?.startsWith(value as string);
          },
        },
        {
          title: 'อำเภอ',
          dataIndex: 'FARM_AMPHUR',
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.FARM_AMPHUR)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.FARM_AMPHUR?.startsWith(value as string);
          },
        },
        {
          title: 'จังหวัด',
          dataIndex: 'FARM_PROVINCE',
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.FARM_PROVINCE)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.FARM_PROVINCE?.startsWith(value as string);
          },
        },
        {
          title: 'ละติจูด',
          dataIndex: 'FARM_LAT',
        },
        {
          title: 'ลองติจูด',
          dataIndex: 'FARM_LNG',
        },
      ] as TableColumnsType<IFarm>,
    [tab, selected, handleUpdateFarmCode],
  );

  const insert = data?.insert;
  const update = data?.update;
  const match = data?.match;
  useEffect(() => {
    const listMap = { create: insert, update, match };
    setSelected(listMap[tab]?.map(c => ({ ...c, _key: uniqueId('b-') })) || []);
    setSelectedRowKeys([]);
  }, [tab, insert, update, match]);

  const rowSelection = {
    onChange: (_: React.Key[], selectedRows: IFarm[]) => {
      setSelectedRowKeys(selectedRows.map(row => row._key));
      setSelectedRows(selectedRows);
    },
    selectedRowKeys,
    getCheckboxProps: (record: IFarm) => ({
      disabled: action === 'match',
    }),
  };

  const [search, SetSearch] = useState('');
  const [searchText1, setSearchText1] = useState('');
  const [searchText2, setSearchText2] = useState('');

  const filteredData = useMemo(() => {
    return selected.filter(item => {
      const farmCode = (item.FARM_ID || '').toLowerCase();
      const searchValue = search.trim().toLowerCase();
      return !searchValue || farmCode.includes(searchValue);
    });
  }, [selected, action, search, searchText1, searchText2]);

  // const onFinish: FormProps<IBrand>['onFinish'] = async values => {
  //   await matchBrandApi.mutateAsync({
  //     ...values,
  //     BRAND_SING_ID: modal.item?.BRAND_SING_ID,
  //   });
  // };

  return (
    <div>
      <Flex justify="space-between" gap={2} className="mb-3">
        <Flex gap={0}>
          <Radio.Group value={tab} onChange={e => setTab(e.target.value)}>
            <Radio.Button value="create">เพิ่ม</Radio.Button>
            <Radio.Button value="update">แก้ไข/ปรับปรุง</Radio.Button>
          </Radio.Group>
        </Flex>
        <Flex gap={12}>
          {tab === 'update' ? (
            <button
              type="button"
              className="btn btn-info"
              style={{ minWidth: 100 }}
              onClick={() => handleDeleteFarms(selectedRows)}
            >
              {actionDeleteFarm.isPending ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <span className="sr-only">Loading...</span>
                </>
              ) : (
                'ลบข้อมูล Farm'
              )}
            </button>
          ) : (
            <></>
          )}

          <button
            type="button"
            className="btn btn-success"
            style={{ minWidth: 100 }}
            disabled={!selectedRowKeys.length}
            onClick={() => handleSyncSelectedFarms(selectedRows)}
          >
            {actionFarmMutation.isPending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                <span className="sr-only">Loading...</span>
              </>
            ) : tab === 'create' ? (
              'ส่งข้อมูลไป OTOD >'
            ) : (
              'แก้ไขข้อมูลที่ OTOD >'
            )}
          </button>
        </Flex>
      </Flex>
      <Table<IFarm>
        rowSelection={{ type: 'checkbox', ...rowSelection }}
        columns={columns}
        dataSource={filteredData}
        size="small"
        bordered
        rowKey={r => r._key}
        pagination={false}
        locale={{ emptyText: 'ไม่พบรายการ' }}
        tableLayout="fixed"
      />
      <Suspense>
        <Modal
          open={modal.open}
          width={350}
          onCancel={() => setModal({ ..._modal })}
          // loading={matchBrandApi.isPending}
          // onOk={() => {
          //   form
          //     .validateFields()
          //     .then(values => {
          //       onFinish(values);
          //     })
          // .catch(() => {
          //       // validation failed
          //     });
          // }}
        >
          <Form
            form={form}
            name="farm"
            layout="vertical"
            // onFinish={onFinish}
            autoComplete="off"
          >
            <Form.Item<IFarm>
              label="FARM CODE"
              name="FARM_ID"
              rules={[{ required: true }]}
            >
              <Select
                style={{ minWidth: 150 }}
                size="large"
                showSearch
                allowClear
                placeholder="ค้นหา"
                filterOption={(input, option) =>
                  ((option?.label as string) ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={[...(insert || [])].sort().map(frm => ({
                  label: `${frm.FARM_ID} - ${frm.FARM_ID}`,
                  value: frm.FARM_ID,
                }))}
              />
            </Form.Item>
          </Form>
        </Modal>
      </Suspense>
    </div>
  );
};

export default FarmPage;
